import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PageHeaderProps {
  title: string;
  description?: string;
  children?: React.ReactNode;
  className?: string;
  /** When set, renders a back button left of the title that navigates to this hash route. */
  backTo?: string;
  /** When set, renders a back button that calls this handler instead of navigating. */
  onBack?: () => void;
  /** Aria-label for the back button. Defaults to "Go back". */
  backLabel?: string;
}

export function PageHeader({
  title,
  description,
  children,
  className,
  backTo,
  onBack,
  backLabel = 'Go back',
}: PageHeaderProps) {
  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    // Pages opened in a new tab (e.g. Product Edit) should close on back/cancel
    // rather than navigate within the popup. window.opener exists when the tab
    // was spawned by another tab.
    if (window.opener) {
      window.close();
      return;
    }
    if (backTo) {
      window.location.hash = backTo;
    } else {
      window.history.back();
    }
  };

  const showBack = backTo !== undefined || onBack !== undefined;

  return (
    <div className={cn('flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between', className)}>
      <div className="flex items-center gap-3">
        {showBack && (
          <button
            type="button"
            onClick={handleBack}
            aria-label={backLabel}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-input bg-white text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        </div>
      </div>
      {children && <div className="flex items-center gap-3">{children}</div>}
    </div>
  );
}
